import React, { FC, ImgHTMLAttributes, useEffect, useState } from 'react';

import cn from 'classnames';

import { Spinner, ZoomableImage } from '@components/common';
import { Models } from '@components/SurveyBuilder';

interface Props extends ImgHTMLAttributes<HTMLImageElement> {
  block?: Models.Block;
  wrapperClassName?: string;
  zoomable?: boolean;
  onLoad?: () => void;
  useNaturalSize?: boolean;
}

const renderImage = ({ zoomable, alt, useNaturalSize, naturalWidth, naturalHeight, ...rest }) => {
  const imageProps = {
    ...rest,
    ...(useNaturalSize && naturalWidth && naturalHeight
      ? {
          style: {
            maxWidth: `min(100%, ${naturalWidth}px)`,
            maxHeight: `min(100%, ${naturalHeight}px)`,
            width: 'auto',
            height: 'auto',
            ...rest.style
          }
        }
      : {})
  };

  return zoomable ? <ZoomableImage alt={alt || ''} {...imageProps} /> : <img alt={alt || ''} {...imageProps} />;
};

export const ImageRenderer: FC<Props> = ({
  block,
  wrapperClassName,
  zoomable,
  alt,
  onLoad,
  useNaturalSize = false,
  ...rest
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string>();
  const [naturalDimensions, setNaturalDimensions] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    setImageLoaded(false);
    setNaturalDimensions(null);

    if (block?.image_url) {
      setCurrentImageUrl(block.image_url);
    } else {
      setCurrentImageUrl(undefined);
    }
  }, [block?.image_url]);

  useEffect(() => {
    if (!currentImageUrl) return;

    const img = new Image();

    img.src = currentImageUrl;

    img.onload = () => {
      setImageLoaded(true);
      if (useNaturalSize) {
        setNaturalDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      }
      onLoad?.();
    };

    return () => {
      img.onload = null;
    };
  }, [currentImageUrl, useNaturalSize]);

  return (
    <>
      {currentImageUrl && (
        <div className={cn('flex items-center justify-center', !imageLoaded && 'bg-white', wrapperClassName)}>
          {imageLoaded ? (
            renderImage({
              zoomable,
              src: currentImageUrl,
              alt: alt || '',
              useNaturalSize,
              naturalWidth: naturalDimensions?.width,
              naturalHeight: naturalDimensions?.height,
              ...rest
            })
          ) : (
            <Spinner className='h-10 w-10' />
          )}
        </div>
      )}
    </>
  );
};
